import { useEffect, useMemo, useRef, useState } from 'react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { Column } from 'primereact/column'
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'
import { Checkbox } from 'primereact/checkbox'
import { Paginator, type PaginatorPageChangeEvent } from 'primereact/paginator'
import Avatar from '../../../components/ui/Avatar'
import ConfirmDialog from '../../../components/ui/ConfirmDialog'
import DataTable from '../../../components/ui/DataTable'
import MobileSheetDropdown from '../../../components/ui/MobileSheetDropdown'
import RefreshButton from '../../../components/ui/RefreshButton'
import { PageHeaderActionsSlot } from '../../../components/ui/pageHeaderActions'
import EmptyState from '../../../components/ui/EmptyState'
import ExcelExportButton from '../../../components/ui/ExcelExportButton'
import FilterBar from '../../../components/ui/FilterBar'
import { formatDate } from '../../../lib/datetime'
import { appStorageKey } from '../../../lib/appStorage'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../../lib/useMediaQuery'
import { exportActions, exportArchivedActions } from '../api/actionApi'
import { useActions, useResponsibleOptions } from '../hooks/useActions'
import { hasListRowActions, useListFollowEnabled } from '../hooks/useActionFollow'
import { ACTION_LIST_DELETE_GROUP, useActionListDelete } from '../hooks/useActionListDelete'
import { useActionStatuses } from '../hooks/useActionStatuses'
import { useActionLevels } from '../hooks/useActionLevels'
import ActionListRowActions from './ActionListRowActions'
import ActionStatusTag from './ActionStatusTag'
import ImportanceBadge from './ImportanceBadge'
import {
  ACTION_STATUS,
  IMPORTANCE_LEVEL,
  mainResponsibleOf,
  secondaryResponsiblesOf,
  subActionResponsibleOf,
  remainingDays,
  ACTION_TAB_SCOPES,
  DASHBOARD_LINK_PARAM,
  type ActionListItem,
  type ActionScope,
  type MinutePerson,
} from '../types'
import { useNavigateWithOrigin } from '../../../app/nav/originNav'
import { useCurrentUser, useGrants } from '../../../auth/useCurrentUser'
import { ROLE_IDS } from '../../../auth/roles'
import { GRANTS } from '../../../auth/grants'
// Satır eylemleri (yıldız + Sil) paylaşılan `.minutes-remove-btn` kalıbını kullanır (Reuse Mandate); sınıf
// minutes.css'te tanımlı olduğu için AÇIKÇA içe aktarılır (ActionListRowActions da aktarır) — bugün tek
// paket üretiliyor ve stil zaten yükleniyor olsa da, rota bazlı bölme açıldığında sessizce stilsiz bir
// düğmeye dönüşürdü.
import '../../minutes/minutes.css'

const PAGE_SIZE = 20

/**
 * **Yönetici kolon kümesi** (kullanıcı kuralı 2026-09-10): Genel Müdür ile GMY/Başkan görev
 * listelerinde YALNIZ bu kolonları görür; kalanlar (Görev No · Önem Derecesi · Toplantı Adı · Süreç
 * Destek Yöneticisi · Ana/Alt Görev · Alt Görev Sayısı) gizlenir. Küme burada, kolonların yanında
 * değil, tek yerde durur ki yeni bir kolon eklendiğinde "yönetici de görecek mi?" sorusu atlanamasın —
 * anahtarı buraya yazmayan her kolon yönetici görünümünde otomatik olarak GİZLİ kalır (fail-closed).
 *
 * <p>Küme yalnız **paylaşılan** kolonları yönetir. "Görev Detayı" buraya yazılmaz çünkü o kolon diğer
 * rollerin tablosunda hiç YOKtur (istenmedi) — kendi kapısı doğrudan `executiveView`dir. **Sil** kolonu
 * da dışarıdadır ve bu bilinçlidir (2026-09-17): küme bir *bilgi* kolonu kümesidir ("yönetici hangi
 * veriyi görsün"), Sil ise bir eylem yüzeyidir ve kendi kapısı yetkidir (`RECORD_DELETE`). Kümeye
 * yazılmaması onu yöneticide gizlemez — `showColumn`dan hiç geçmez. Tersi, silme yetkisi olan dört
 * rolden biri olan **Genel Müdür**'den düğmeyi saklardı, yani özelliği tam da isteyen kullanıcıda
 * kapatırdı.
 */
const EXECUTIVE_COLUMNS = new Set(['name', 'mainResponsible', 'deadline', 'remaining', 'status'])

/**
 * Yönetici kolon kümesinin uygulandığı kapsamlar — sol menüdeki dört görev alt sayfası (kullanıcı
 * kuralı 2026-09-10: kural "Tüm Görevler"le başladı, aynı gün Oluşturduklarım · Bana Atananlar · Takip
 * Ettiklerim'e de genişletildi).
 *
 * <p><b>Liste `ACTION_TAB_SCOPES`'tan türetilir, elle YAZILMAZ</b> (push kapısı review bulgusu): o sabit
 * zaten "sol menüdeki görev sekmeleri"nin tek kaynağıdır ve kuralın kapsamı birebir odur. Kopya bir dizi
 * bugün eşit olsa da yeni bir sekme eklendiğinde sessizce ayrışırdı — yeni sekme menüde çıkar, yönetici
 * kolon kuralı ona uygulanmazdı.
 *
 * <p><b>`delegation` bu yüzden zaten DIŞARIDA</b> ve gerekçesi aynı yerde duruyor: o ekran sol menüde
 * değil, Anasayfa'daki Delegasyon Özeti kartının "Tümünü Gör" hedefidir ve `ACTION_TAB_SCOPES`'a bilinçle
 * eklenmemiştir (bkz. `types.ts` → `ActionScope`). Varlık sebebi satırın hangi kümeden geldiğini söyleyen
 * **"Tür"** kolonudur; kolon kümesi oraya uygulanınca ekranın tek ayırt edici bilgisi kaybolurdu.
 */
const EXECUTIVE_SCOPES: readonly ActionScope[] = ACTION_TAB_SCOPES

/**
 * Yönetici kolon kümesinin uygulandığı roller — {@link ROLE_IDS}. Ölçüt rol KİMLİĞİdir, ad değil
 * (ad yönetim ekranından değiştirilebilir; bkz. roles.ts). Yetkiyle (grant) ayrılamaz: aynı yetkileri
 * Yönetici/Özel Kalem rolleri de taşır, oysa kural yalnız bu iki role bakar.
 */
const EXECUTIVE_ROLE_IDS: readonly number[] = [
  ROLE_IDS.GENEL_MUDUR,
  ROLE_IDS.GENEL_MUDUR_YARDIMCISI_VE_BASKAN,
]

/** Arşiv sayfasında statü filtresi yalnız bu iki statüden seçilebilir (analiz 8.5). */
/** İzin verilen Önem Derecesi id'leri (REFERANS TYPE=IMPORTANCE_LEVEL) — URL filtresi doğrulaması için. */
const IMPORTANCE_LEVEL_IDS = Object.values(IMPORTANCE_LEVEL) as number[]

/** URL'den önem filtresi (geçersiz/boş → filtre yok). */
function parseImportance(raw: string | null): number | null {
  if (!raw) return null
  const value = Number(raw)
  return Number.isInteger(value) && IMPORTANCE_LEVEL_IDS.includes(value) ? value : null
}

/** URL'den durum filtresi ("330,332"); geçersiz değerler atılır. */
function parseStatusIds(raw: string | null): number[] {
  if (!raw) return []
  return raw
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((value) => Number.isInteger(value) && value > 0)
}

/**
 * Kapsam-başına görünüm depo anahtarı (kullanıcı/persona geçişinde ön-ek taramasıyla temizlenir —
 * 062 D7a; temizlik `ceosuite:` önekini tarar, buradaki alt adı değil, o yüzden sürümlemek güvenli).
 *
 * <p><b>`v2` (24.09.2026):</b> "Bana Atananlar" varsayılan statü süzgeci eklenince (bkz.
 * {@link defaultStatusIds}) eski kayıtlar bir soruna dönüştü. Varsayılan yalnız <b>hiç kayıt yokken</b>
 * uygulanır — kayıt varsa kullanıcının seçimi sayılır. Ama `v1` kayıtları varsayılan daha <b>ortada
 * yokken</b> yazılmıştı ve ekranı bir kez açmış olmak (kayıt mount'ta da yazılır) yeterliydi; yani
 * "kullanıcı süzgeci kaldırdı" ile "böyle bir varsayılan henüz yoktu"yu ayırt edemiyorlar. Sonuç:
 * sekmesi açık olan herkeste varsayılan hiç görünmedi (kullanıcı bildirimi). Anahtarı sürümlemek o
 * ayrımı temiz kurar: `v1` kayıtları okunmaz, herkes bir kez varsayılanla başlar, sonraki seçimler
 * yine korunur. Bedeli tek seferlik bir sıfırlamadır (sıralama/sayfa boyutu) ve depo zaten sekme
 * ömürlüdür. Eski kayıtlar silinMEZ: aynı temizlik taraması onları persona geçişinde zaten alır.
 */
function viewStorageKey(scope: ActionScope, archived: boolean): string {
  return appStorageKey(`actions-list-view:v2:${archived ? 'arch' : 'active'}:${scope}`)
}

/** Kalıcı/deep-link görünüm durumu (filtre + arama + sayfa + boyut + sıralama). */
interface ActionView {
  q: string
  importanceLevelId: number | null
  statusIds: number[]
  criticalOrOverdue: boolean
  /**
   * Anasayfa panosunun "Tümünü Gör" bağlantılarından gelen süzgeçler (2026-08-21). Hepsi URL-güdümlüdür
   * ve chip olarak görünüp kaldırılabilir; amaç hedef tablonun kartla **aynı** kümeyi göstermesi.
   */
  overdueOnly: boolean
  upcomingOnly: boolean
  delegatedOnly: boolean
  /** Dönem anahtarı (ör. `LAST_1_MONTH`) + `CUSTOM` için tarihler; boş → pencere yok. */
  period: string | null
  periodFrom: string | null
  periodTo: string | null
  kind: 'all' | 'main' | 'sub'
  delayedUpdatedOnly: boolean
  /** Ana Sorumlu süzgeci (sicil, rol 340); seçim yokken null. */
  mainResponsibleEmployeeId: string | null
  /** Süreç Destek Yöneticisi süzgeci (sicil; DB'de "Yan Sorumlu", rol 341); seçim yokken null. */
  supportManagerEmployeeId: string | null
  first: number
  rows: number
  sortField?: string
  sortOrder?: 1 | -1
}

/** Sorgu-dizesini görünüm durumuna çevirir (hem URL deep-link'i hem sessionStorage için ortak biçim). */
function parseView(sp: URLSearchParams): ActionView {
  const kindRaw = sp.get('kind')
  const kind: 'all' | 'main' | 'sub' = kindRaw === 'main' || kindRaw === 'sub' ? kindRaw : 'all'
  const sortField = sp.get('sortField') || undefined
  const sortOrder: 1 | -1 | undefined = sortField
    ? sp.get('sortOrder') === 'asc'
      ? 1
      : -1
    : undefined
  const first = Number(sp.get('first'))
  const rows = Number(sp.get('size'))
  return {
    q: sp.get('q') ?? '',
    importanceLevelId: parseImportance(sp.get('importanceLevelId')),
    statusIds: parseStatusIds(sp.get('statusIds')),
    criticalOrOverdue: sp.get('criticalOrOverdue') === 'true',
    overdueOnly: sp.get('overdue') === 'true',
    upcomingOnly: sp.get('upcoming') === 'true',
    delegatedOnly: sp.get('delegated') === 'true',
    period: sp.get('period')?.trim() || null,
    periodFrom: sp.get('from')?.trim() || null,
    periodTo: sp.get('to')?.trim() || null,
    kind,
    delayedUpdatedOnly: sp.get('delayedUpdated') === 'true',
    // Anahtarlar 2026-09-15'te değişti (tek `responsible` → iki ayrı süzgeç). Eski bir yer imi /
    // kayıtlı görünüm artık eşleşmez ve süzgeçsiz açılır — bilinçli: eski değerin hangi role ait
    // olduğu bilinemez, ikisine birden yazmak VE ile kesişip boş liste üretirdi.
    mainResponsibleEmployeeId: sp.get('mainResponsible')?.trim() || null,
    supportManagerEmployeeId: sp.get('supportManager')?.trim() || null,
    first: Number.isInteger(first) && first > 0 ? first : 0,
    rows: Number.isInteger(rows) && rows > 0 ? rows : PAGE_SIZE,
    sortField,
    sortOrder,
  }
}

/** Yalnız `true` iken yazılan bayrak — {@link parseView} bu anahtarlarda `'true'` bekler. */
function flagParam(on: boolean): 'true' | undefined {
  return on ? 'true' : undefined
}

/** Sıralama yönü `asc`/`desc` olarak taşınır; sıralama alanı yoksa yön de yazılmaz. */
function sortOrderParam(view: ActionView): string | undefined {
  if (!view.sortField) return undefined
  return view.sortOrder === 1 ? 'asc' : 'desc'
}

/**
 * Görünüm durumu → sorgu-dizesi: {@link parseView}'in **tersi** ve onunla aynı anahtar kümesini
 * kullanır (biri değişirse diğeri de değişmek zorundadır — alanlar tek listede durduğu için gözden
 * kaçmaz). Varsayılan/boş değerler hiç yazılmaz: depoda yalnız kullanıcının gerçekten seçtiği
 * süzgeçler kalır.
 */
function serializeView(view: ActionView): string {
  const entries: Array<[string, string | number | null | undefined]> = [
    // `q` BİLEREK YOK: arama metni kalıcı değildir (kullanıcı kuralı 2026-08-31) — bkz. initialView.
    ['importanceLevelId', view.importanceLevelId],
    ['statusIds', view.statusIds.join(',')],
    ['criticalOrOverdue', flagParam(view.criticalOrOverdue)],
    ['overdue', flagParam(view.overdueOnly)],
    ['upcoming', flagParam(view.upcomingOnly)],
    ['delegated', flagParam(view.delegatedOnly)],
    ['period', view.period],
    ['from', view.periodFrom],
    ['to', view.periodTo],
    ['kind', view.kind === 'all' ? undefined : view.kind],
    ['delayedUpdated', flagParam(view.delayedUpdatedOnly)],
    ['mainResponsible', view.mainResponsibleEmployeeId],
    ['supportManager', view.supportManagerEmployeeId],
    ['first', view.first > 0 ? view.first : undefined],
    ['size', view.rows === PAGE_SIZE ? undefined : view.rows],
    ['sortField', view.sortField],
    ['sortOrder', sortOrderParam(view)],
  ]
  const params = new URLSearchParams()
  for (const [key, value] of entries) {
    if (value != null && value !== '') params.set(key, String(value))
  }
  return params.toString()
}

/** Görünümü kapsam-başına depoya yazar; depo kullanılamıyorsa kalıcılık sessizce atlanır. */
function persistView(scope: ActionScope, archived: boolean, view: ActionView): void {
  try {
    sessionStorage.setItem(viewStorageKey(scope, archived), serializeView(view))
  } catch {
    /* sessionStorage kullanılamıyorsa kalıcılık atlanır */
  }
}

/** Panodan gelen süzgeç chip'lerinin anahtarları — etiket üretimi ile "kaldır" eylemi bununla eşlenir. */
type PanelChipKey = 'overdue' | 'upcoming' | 'delegated' | 'period'

/** Chip'lerin okuduğu süzgeç alt kümesi (görünümün tamamı gerekmez). */
type PanelChipFilters = Pick<
  ActionView,
  'overdueOnly' | 'upcomingOnly' | 'delegatedOnly' | 'period' | 'periodFrom' | 'periodTo'
>

/**
 * Dönem chip'inin etiketi: `CUSTOM` iken tarih aralığı, aksi halde **dönem adı**
 * (`home.period.<anahtar>` — Anasayfa panosunun seçicisiyle AYNI etiket kaynağı).
 */
function periodChipLabel(filters: PanelChipFilters, t: TFunction): string {
  if (filters.period === 'CUSTOM' && filters.periodFrom && filters.periodTo) {
    return `${formatDate(filters.periodFrom)} - ${formatDate(filters.periodTo)}`
  }
  return t(`home.period.${filters.period}`)
}

/**
 * Panodan gelen süzgeçlerin chip listesi (2026-08-21). Tek dizide toplanır: dördü de aynı davranışa
 * sahip (etiket + kaldır) ve JSX'te dört ayrı blok yazmak ilk değişiklikte ayrışmaya davetiye olurdu.
 *
 * <p>Dönem chip'inin etiketi {@link periodChipLabel}'dedir.
 *
 * <p>Etiket üretimi burada **saf** kalır; "kaldır" eylemi çağıranın {@link PanelChipKey} → setter
 * eşlemesindedir (state'e yalnız bileşen dokunur).
 */
function panelChipsOf(
  filters: PanelChipFilters,
  t: TFunction,
): Array<{ key: PanelChipKey; label: string }> {
  const chips: Array<{ key: PanelChipKey; label: string } | false> = [
    filters.overdueOnly && { key: 'overdue', label: t('actions.filter.overdue') },
    filters.upcomingOnly && { key: 'upcoming', label: t('actions.filter.upcoming') },
    filters.delegatedOnly && { key: 'delegated', label: t('actions.filter.delegated') },
    // Etiket yalnız dönem SEÇİLİYKEN üretilir; aksi halde i18next'e `home.period.null` sorulurdu.
    filters.period != null && { key: 'period', label: periodChipLabel(filters, t) },
  ]
  return chips.filter((chip): chip is { key: PanelChipKey; label: string } => chip !== false)
}

/**
 * URL, görünümü **kendisi** belirliyor mu? (Anasayfa panosu bağlantısı) — öyleyse storage'daki son görünüm
 * yok sayılır ve ekran yalnız URL'in söylediği süzgeçlerle açılır.
 *
 * <p><b>{@link DASHBOARD_LINK_PARAM} (2026-08-21, kullanıcı bildirimi "Tümünü Gör ile gidilen sayfaların
 * filtreleri"):</b> panonun bağlantılarının çoğu <b>süzgeçsiz</b> ("bu kapsamın tamamını göster") demek
 * ister ve o hâlde bu fonksiyon `false` dönüyordu → ekran kullanıcının o kapsamda <b>en son</b> kullandığı
 * aramayı/statü süzgecini sessizce geri yüklüyordu. Sonuç, kartta 5 satır görüp "Tümünü Gör"e basınca
 * eskiden kalma bir süzgeç yüzünden 1 satır (ya da hiç) görmekti. İşaretçi, "süzgeci olmayan bir
 * deep-link" ile "hiç deep-link yok"u ayırt eder.
 */
function hasDeepLinkFilters(sp: URLSearchParams): boolean {
  return (
    sp.has(DASHBOARD_LINK_PARAM) ||
    sp.has('q') ||
    sp.has('importanceLevelId') ||
    sp.has('statusIds') ||
    sp.has('criticalOrOverdue') ||
    sp.has('overdue') ||
    sp.has('upcoming') ||
    sp.has('delegated') ||
    sp.has('period') ||
    // Yönetici Raporu'nun "Tümünü Gör"ü panonun kişi süzgecini buraya taşır (2026-08-31). Listede
    // olduğu gibi tüketilir: aşağıdaki tek-kullanımlık temizlik onu da URL'den siler, aksi hâlde
    // süzgeç chip'ten kaldırılsa bile ekrana her dönüşte geri gelirdi.
    //
    // ANAHTAR 2026-09-15'te DEĞİŞTİ (`responsible` → ikiye ayrıldı). Burayı güncellememek sessiz bir
    // hata üretirdi: bağlantı "deep-link değil" sayılır, kayıtlı görünüm kazanır ve kişi süzgeci
    // hiç uygulanmazdı. Eski anahtar bilinçle BIRAKILMADI — onu tanımak, hangi role ait olduğu
    // bilinmeyen bayat bir değeri geri getirirdi.
    sp.has('mainResponsible') ||
    sp.has('supportManager')
  )
}

/**
 * Başlangıç görünümü: URL deep-link'i öncelikli; yoksa sessionStorage'daki son görünüm; o da yoksa
 * kapsamın varsayılanı ({@link defaultStatusIds} — Bana Atananlar'da Aksiyon Bekleniyor, diğerlerinde boş).
 *
 * <p><b>Arama metni (`q`) depodan GERİ YÜKLENMEZ</b> (kullanıcı kuralı 2026-08-31: *"filtre alanına
 * eklenen text sayfadan ayrılıp başka sayfaya gidilip dönüldüğünde sıfırlanmalı"*). Diğer süzgeçler
 * (önem/durum/sorumlu/sayfa/sıralama) kalıcı kalır — kural yalnız metni kapsar.
 *
 * <p>Sıfırlama iki yerden birlikte zorlanır: {@link serializeView} `q`'yu hiç YAZMAZ, burada da
 * okunan değer boşlanır. İkisi de gerekli: yalnız yazmayı kesmek, bu değişiklikten ÖNCE yazılmış bir
 * oturum kaydını (sessionStorage sekme ömrü boyunca yaşar) bir kez daha geri yüklerdi.
 *
 * <p>URL'den gelen `q` **etkilenmez**: deep-link (`?q=...`) hâlâ aramayı doldurur — o, kullanıcının o
 * anda istediği bir şeydir, "eskiden kalma" değil.
 */
/**
 * Kapsamın <b>ilk açılış</b> statü süzgeci. "Bana Atananlar" <b>Aksiyon Bekleniyor</b> ile açılır
 * (24.09.2026 kullanıcı isteği): kullanıcının kendi listesinde aradığı şey neredeyse her zaman
 * "bende ne var, ne yapmam gerekiyor"dur; tamamlanmış ve kapatılmış görevler o soruyu gürültüye
 * boğuyordu. Diğer kapsamlar süzgeçsiz açılır — "Tüm Görevler" ve "Oluşturduklarım" bütünü görmek
 * içindir, oraya varsayılan bir süzgeç koymak kayıt saklardı.
 *
 * <p><b>Arşivde uygulanMAZ:</b> orada statü süzgeci hiç yoktur ve küme zaten Arşivlendi/İptal'dir;
 * 330 oraya konsa sunucuya gitmeden düşerdi ama chip'te görünüp kullanıcıyı yanıltırdı.
 */
function defaultStatusIds(scope: ActionScope, archived: boolean): number[] {
  return scope === 'assigned' && !archived ? [ACTION_STATUS.PENDING] : []
}

function initialView(scope: ActionScope, archived: boolean, url: URLSearchParams): ActionView {
  if (hasDeepLinkFilters(url)) return parseView(url)
  let stored: string | null
  try {
    stored = sessionStorage.getItem(viewStorageKey(scope, archived))
  } catch {
    stored = null
  }
  // Depoda HİÇ kayıt yok (bu sekmede kapsam ilk kez açılıyor) → kapsamın varsayılanı. Kayıt VARSA
  // ona dokunulmaz, boş olsa bile: boş kayıt "kullanıcı süzgeci kaldırdı" demektir ve varsayılanı
  // yeniden basmak, kullanıcının az önce yaptığı seçimi geri alırdı. `null` ile `''` ayrımı bu
  // yüzden korunur — ikisini `?? ''` ile birleştirmek tam bu hatayı üretir.
  if (stored === null) {
    return { ...parseView(new URLSearchParams()), statusIds: defaultStatusIds(scope, archived) }
  }
  return { ...parseView(new URLSearchParams(stored)), q: '' }
}

interface Props {
  /**
   * Alt-sayfa kapsamı (all/created/assigned/followed). "created" (Oluşturduklarım) **ve** "assigned"
   * (Bana Atananlar) hem ana hem alt aksiyon içerir → Tür filtresi + Ana/Alt aksiyon kolonu bu iki
   * kapsamda gösterilir. Bana Atananlar'da eşleşme **role** göredir: kökte Ana Sorumlu, altta atanan
   * (2026-08-17 düzeltmesi — eskiden sekme yalnız alt aksiyon istiyordu ve Ana Sorumlu'yu hiç
   * göstermiyordu). Diğer kapsamlar yalnız kök-aksiyon döndürür.
   */
  scope: ActionScope
  /** true → Arşivlenen Aksiyonlar (salt-okuma; Arşivlendi/İptal). Detay arşiv rotasına gider. */
  archived?: boolean
}

/**
 * Aksiyon listesi paneli (analiz 6.4/6.5/6.7) — Tüm Aksiyonlar / Oluşturduklarım / Takip Ettiklerim sekmeleri
 * bu tek paneli farklı {@link Props.scope} ile besler. Sunucu-taraflı sayfalama + sıralama + filtre (önem/durum)
 * + arama (ad/toplantı adı); veri kapsamı sunucuda {@code scope} + giriş yapan kullanıcıya göre süzülür.
 * Satır → Aksiyon Detay. <b>Sorumlu</b> kolonu MINUTES_ASSIGNEE'den doldurulur (sicil bazında çoklu).
 */
export default function ActionsListPanel({ scope, archived = false }: Readonly<Props>) {
  const { t, i18n } = useTranslation()
  // Toplantıya bağlı olmayan görevde (`meetingId` null) Toplantı Adı yerine "Doğrudan Görev" yazılır.
  const meetingLabel = (row: ActionListItem) =>
    row.meetingId == null ? t('actions.columns.meetingDirect') : (row.meetingName ?? '')
  // Ana Sorumlu dropdown'ının arama yereli — Türkçe-farkında süzme için (bkz. AssignmentPickList).
  const filterLocale = i18n.language.startsWith('en') ? 'en' : 'tr'
  // Detaya giderken bu listenin konumu (path + URL query'si) origin taşınır → Aksiyon Detay'da "Geri" buraya
  // döner. Kapsam artık ROTADADIR (/minutes/actions/<scope>) → origin kullanıcının ayrıldığı alt sayfayı korur.
  // Ancak bu paneldeki etkileşimli filtreler (arama/önem/durum) React state'te olup URL'e YAZILMAZ → origin
  // onları TAŞIMAZ (yalnız deep-link query'si, ör. ?criticalOrOverdue=true, taşınır); filtre kalıcılığı ayrı bir iş.
  const navigate = useNavigateWithOrigin()
  const [searchParams, setSearchParams] = useSearchParams()
  // 077 — Kayıt No kolonu yalnız RECORD_NUMBER_VIEW yetkisinde (Özel Kalem); diğerlerinde gizli.
  const showRecordNumber = useGrants().hasGrant(GRANTS.RECORD_NUMBER_VIEW)
  /**
   * Görev silme (2026-09-17) — `undefined` iken çöp kutusu hiç çizilmez. Koşullar (yetki
   * `RECORD_DELETE` + kapsam + arşiv değil) bilinçle hook'tadır: silmenin tüm kuralları tek dosyada
   * dursun ve bu bileşenin dallanması artmasın. Satır başına ikinci kapı `row.deletable`dır; bayrak
   * kök ve alt görev satırında AYNI ölçüte bakar (yetki), hangi ucun çağrılacağını satırın
   * `subAction` alanı belirler.
   */
  const onDelete = useActionListDelete(scope, archived)
  /** 120 — satırdaki yıldız (kendi adına takip); kapsam/kullanıcı koşulları hook'ta. */
  const followEnabled = useListFollowEnabled(scope, archived)
  // Delegasyon kapsamında "Tür" kolonu için: satır bana mı atanmış, yoksa ben mi devretmişim.
  const { data: me } = useCurrentUser()
  /**
   * IT Admin görev içeriği maskesi (129): başlık/açıklama `***` gelir ve sunucu bu iki alana göre
   * sıralamaz. Sıralama oku çizilmez — çalışmayacak kontrol sunulmaz.
   */
  const contentMasked = me?.actionContentMasked === true
  /**
   * Yönetici kolon kümesi devrede mi (kullanıcı kuralı 2026-09-10)? {@link EXECUTIVE_SCOPES}'teki dört
   * alt sayfada ve yalnız **aktif** listede: arşiv ayrı bir sayfadır (kendi "Geciken" kolonu vardır) ve
   * kural onu kapsamıyordu.
   *
   * <p>`roleIds` gelmemişse (eski backend'e karşı çalışan yeni frontend — bkz. `MeResponse.roleIds`)
   * `?? []` ile hiçbir kolon gizlenmez: gizleme kozmetiktir, fail-open doğru yöndür — GM'ye eksik kolon
   * göstermektense fazla göstermek yeğdir.
   */
  const executiveView =
    EXECUTIVE_SCOPES.includes(scope) &&
    !archived &&
    (me?.roleIds ?? []).some((id) => EXECUTIVE_ROLE_IDS.includes(id))
  /** Kolon render kapısı — yönetici görünümünde yalnız {@link EXECUTIVE_COLUMNS} çizilir. */
  const showColumn = (key: string) => !executiveView || EXECUTIVE_COLUMNS.has(key)

  // Sorumlu süzgecinin "X" (Temizle) düğmesi mobilde satıra TIKLAMA gibi davranıyordu (kullanıcı
  // bildirimi 2026-08-29): temizleme filtre bar'ının/kart listesinin yüksekliğini anında değiştiriyor,
  // dokunuşun ürettiği native `click` ise (pointerup'tan SONRA, PrimeReact'in kendi `onPointerUp`
  // tabanlı temizleme mantığından bağımsız olarak) parmağın kaldığı KOORDİNATTA yeniden hedefleniyor —
  // o an orada artık yukarı kaymış bir satır/kart olduğu için "detaya git" tetikleniyordu. Kök neden
  // tarayıcıya özgü bir "hayalet tık" olduğundan (olayı iptal etmek çözmez — farklı bir olay), satır
  // navigasyonu burada kısa bir pencere boyunca bastırılır.
  const suppressRowClickUntilRef = useRef(0)
  const suppressRowClickBriefly = () => {
    suppressRowClickUntilRef.current = Date.now() + 400
  }
  const goToDetail = (path: string) => {
    if (Date.now() < suppressRowClickUntilRef.current) return
    navigate(path)
  }

  // Başlangıç görünümü (filtre/arama/sayfa/boyut/sıralama) mount'ta bir kez hesaplanır: URL deep-link'i
  // varsa (053 — Anasayfa panosu `?criticalOrOverdue=true`) ondan; yoksa sessionStorage'daki son görünümden
  // (kapsam-başına kalıcı — bilet talep 016 deseni); o da yoksa kapsamın varsayılan süzgeci
  // (Bana Atananlar → Aksiyon Bekleniyor). Sonrasını kullanıcı etkileşimi yönetir.
  const initialRef = useRef<ActionView | null>(null)
  if (initialRef.current === null) initialRef.current = initialView(scope, archived, searchParams)
  const initial = initialRef.current

  // "Bu mount deep-link ile mi açıldı?" — MOUNT'ta bir kez donar. Aşağıdaki tek-kullanımlık tüketim
  // effect'i deep-link parametrelerini URL'den SİLDİĞİ için, storage koruması `searchParams`'ı canlı
  // okuyamaz: silme sonrası ikinci render'da "deep-link yok" der ve pano bağlantısıyla gelen süzgeci
  // kapsamın storage'ına yazardı → kullanıcı o sekmeye sorgusuz döndüğünde süzgeç geri gelirdi
  // (tam olarak MINÖR#1'in ve `hasDeepLinkFilters` javadoc'undaki "Tümünü Gör" şikâyetinin engellediği
  // sızıntı, bu kez URL yerine storage üzerinden). Ref, tüketimden ÖNCEKİ gerçeği taşır.
  const deepLinkDrivenRef = useRef(hasDeepLinkFilters(searchParams))

  const [search, setSearch] = useState(initial.q)
  const [debounced, setDebounced] = useState(initial.q)
  const [importanceLevelId, setImportanceLevelId] = useState<number | null>(
    initial.importanceLevelId,
  )
  const [statusIds, setStatusIds] = useState<number[]>(initial.statusIds)
  const [criticalOrOverdue, setCriticalOrOverdue] = useState(initial.criticalOrOverdue)
  // 2026-08-21 — Anasayfa panosunun "Tümünü Gör" bağlantılarının süzgeçleri; her biri chip ile kaldırılır.
  const [overdueOnly, setOverdueOnly] = useState(initial.overdueOnly)
  const [upcomingOnly, setUpcomingOnly] = useState(initial.upcomingOnly)
  const [delegatedOnly, setDelegatedOnly] = useState(initial.delegatedOnly)
  const [period, setPeriod] = useState<string | null>(initial.period)
  const [periodFrom] = useState<string | null>(initial.periodFrom)
  const [periodTo] = useState<string | null>(initial.periodTo)
  // 071 — Tür (Ana/Alt) filtresi; hem ana hem alt içeren kapsamlarda anlamlı: Oluşturduklarım ve (2026-08-17'den beri) Bana Atananlar.
  const [kind, setKind] = useState<'all' | 'main' | 'sub'>(initial.kind)
  // Arşivde "Geciken" filtresi (checkbox): işaretliyse yalnız IS_DELAYED_UPDATED=Evet satırlar (df7bd6a).
  const [delayedUpdatedOnly, setDelayedUpdatedOnly] = useState(initial.delayedUpdatedOnly)
  // Kişi süzgeçleri — yalnız aktif alt sayfalarda; arşivde yoktur (bkz. responsibleFilterApplicable).
  // 2026-09-15'te AYRILDI (kullanıcı isteği): önceden tek kutu Ana VEYA Yan rolünde arıyordu.
  const [mainResponsibleEmployeeId, setMainResponsibleEmployeeId] = useState<string | null>(
    initial.mainResponsibleEmployeeId,
  )
  const [supportManagerEmployeeId, setSupportManagerEmployeeId] = useState<string | null>(
    initial.supportManagerEmployeeId,
  )
  const [first, setFirst] = useState(initial.first)
  const [rows, setRows] = useState(initial.rows)
  const [sortField, setSortField] = useState<string | undefined>(initial.sortField)
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0 | null | undefined>(initial.sortOrder)

  // Arama 300ms debounce. İlk mount atlanır ki storage'dan geri yüklenen arama/sayfa sıfırlanmasın.
  const searchMounted = useRef(false)
  useEffect(() => {
    if (!searchMounted.current) {
      searchMounted.current = true
      return
    }
    const id = setTimeout(() => {
      setDebounced(search)
      setFirst(0)
    }, 300)
    return () => clearTimeout(id)
  }, [search])

  // Deep-link sorgu parametreleri TEK KULLANIMLIKTIR: `initial` onları yukarıda bir kez okudu; burada URL'den
  // silinir. Aksi halde bir chip kaldırılsa bile (state temizlenir, URL temizlenmez) bu ekrana her dönüşte
  // (ör. Aksiyon Detay'daki "Geri") aynı bayat parametre yeniden okunup filtre geri gelirdi (kullanıcı
  // bildirimi — "delegated only filtresi kaldırılsa bile tekrar navigasyonda ekleniyor").
  useEffect(() => {
    if (!hasDeepLinkFilters(searchParams)) return
    const next = new URLSearchParams(searchParams)
    ;[
      DASHBOARD_LINK_PARAM,
      'q',
      'importanceLevelId',
      'statusIds',
      'criticalOrOverdue',
      'overdue',
      'upcoming',
      'delegated',
      'period',
      'from',
      'to',
      'mainResponsible',
      'supportManager',
    ].forEach((key) => next.delete(key))
    setSearchParams(next, { replace: true })
    // Yalnız mount'ta bir kez: deep-link tüketimi kalıcı bir senkronizasyon değil, tek seferlik bir temizliktir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Görünüm durumu → sessionStorage (kapsam-başına): F5 ve başka ekrana gidip dönmede korunur (bilet talep 016).
  // Deep-link (URL) ile gelen görünüm URL-güdümlüdür → storage'a YAZILMAZ; aksi halde pano bağlantısıyla gelen
  // filtre, sekme gezildikçe diğer kapsamların storage'ına sızardı (code-review MINÖR#1).
  useEffect(() => {
    if (deepLinkDrivenRef.current) return
    persistView(scope, archived, {
      q: debounced,
      importanceLevelId,
      statusIds,
      criticalOrOverdue,
      overdueOnly,
      upcomingOnly,
      delegatedOnly,
      period,
      periodFrom,
      periodTo,
      kind,
      delayedUpdatedOnly,
      mainResponsibleEmployeeId,
      supportManagerEmployeeId,
      first,
      rows,
      sortField,
      sortOrder: sortOrder === 1 ? 1 : -1,
    })
  }, [
    debounced,
    importanceLevelId,
    statusIds,
    criticalOrOverdue,
    overdueOnly,
    upcomingOnly,
    delegatedOnly,
    period,
    periodFrom,
    periodTo,
    kind,
    delayedUpdatedOnly,
    mainResponsibleEmployeeId,
    supportManagerEmployeeId,
    first,
    rows,
    sortField,
    sortOrder,
    scope,
    archived,
  ])

  const sort = sortField ? `${sortField},${sortOrder === 1 ? 'asc' : 'desc'}` : undefined
  // Tür (Ana/Alt) filtresi + kolonu, hem ana hem alt aksiyon içeren kapsamlarda anlamlı: Oluşturduklarım
  // (created) ve Bana Atananlar (assigned).
  //
  // BUG DÜZELTMESİ 2026-08-17 — burada `scope === 'assigned' ? true : …` vardı, yani sekme uçtan YALNIZCA
  // alt aksiyon istiyordu (kullanıcı kuralı 2026-08-06). O kural 2026-08-12'de sessizce geçersizleşti:
  // otomatik alt aksiyon artık yalnız YAN Sorumlu için üretiliyor, Ana Sorumlu doğrudan ana aksiyondan
  // sorumlu. Ana Sorumlu her aksiyonda ZORUNLU olduğu için sekme çoğu kullanıcıya BOŞ geliyordu
  // (kullanıcı bildirimi). Tekrar sorunu (Yan Sorumlu'nun hem kök hem alt satırı görmesi) artık istemci
  // filtresiyle değil sunucudaki ROL-FARKINDA yüklemle çözülüyor: kökte yalnız Ana Sorumlu eşleşir.
  // `delegation` DAHİL (push kapısı review bulgusu): sunucuda o kapsam da alt görevleri içeriyor
  // (devir çoğunlukla alt görevde yapılır) → satırın Ana mı Alt mı olduğu görünmeli ve süzülebilmeli.
  const kindApplicable = scope === 'created' || scope === 'assigned' || scope === 'delegation'
  // Tür seçilmemişse (all) süzme yok → null. Sunucu Bana Atananlar'da iş-kalemi ölçütünü kendi uygular.
  const subAction = kindApplicable && kind !== 'all' ? kind === 'sub' : null
  // Ana Sorumlu süzgeci yalnız aktif dört alt sayfada: arşiv ucu (/archived) kapsam almaz, bu süzgeci de
  // desteklemez → orada gösterilmez ve gönderilmez (bayat storage/deep-link değeri de böylece etkisizdir).
  const responsibleFilterApplicable = !archived
  // Arşivde statü süzgeci yoktur → bayat storage/deep-link değeri de sunucuya gitmez.
  const statusFilter = archived ? [] : statusIds
  const mainResponsibleSicil = responsibleFilterApplicable ? mainResponsibleEmployeeId : null
  const supportManagerSicil = responsibleFilterApplicable ? supportManagerEmployeeId : null
  const list = useActions(
    {
      scope,
      q: debounced,
      importanceLevelId,
      statusIds: statusFilter,
      criticalOrOverdue,
      overdueOnly,
      upcomingOnly,
      delegatedOnly,
      period: period ?? undefined,
      periodFrom,
      periodTo,
      subAction,
      delayedUpdated: delayedUpdatedOnly,
      mainResponsibleEmployeeId: mainResponsibleSicil,
      supportManagerEmployeeId: supportManagerSicil,
      page: Math.floor(first / rows),
      size: rows,
      sort,
    },
    archived,
  )

  const items = list.data?.content ?? []
  const total = list.data?.page.totalElements ?? 0

  /**
   * **Boş sayfada kalma** (2026-09-17, push kapısı review bulgusu): sayfanın son satırı silinince liste
   * aynı `first` ile yeniden çekilir ve kullanıcı, sayfalayıcı "21 kayıt" derken boş bir ekranda kalır —
   * elle Önceki'ye basmak zorunda. Bir sayfa geri kayılır; küme hâlâ boşsa etki tekrarlanıp 0'a iner.
   *
   * <b>Ölçüt `isSuccess`, `!isLoading` DEĞİL</b> (Duo bulgusu, MR !221): TanStack Query v5'te
   * `isLoading` yalnız ilk yüklemede true'dur — istek **hata** verdiğinde de `false` olur ve `data`
   * gelmediği için `items` boş kalır. `!isLoading` ile yazıldığında ağ hatası da "sayfa boş" sayılır
   * ve kullanıcının sayfa konumu, o sayfa hiç yüklenmemişken sessizce geri kaydırılırdı. `isSuccess`
   * yalnız "sunucu gerçekten boş küme döndü" hâlini kapsar; aynı dosyadaki kişi süzgeci effect'leri
   * de (`mainResponsibles.isSuccess`, `supportManagers.isSuccess`) tam bu sebeple öyle yazılmıştı.
   */
  useEffect(() => {
    if (list.isSuccess && items.length === 0 && first > 0) {
      setFirst(Math.max(0, first - rows))
    }
  }, [list.isSuccess, items.length, first, rows])
  // Telefon genişliğinde yoğun 9-kolonlu tablo yerine kart listesi (bkz. actions.css `.act-cards`) —
  // aynı `items`/sayfalama/silme; yalnız sunum dalı değişir. jsdom testte `matchMedia` her zaman
  // `false` döndüğü için (test/setup.ts) testler hep masaüstü dalını görür.
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  const statuses = useActionStatuses()
  const statusList = useMemo(() => statuses.data ?? [], [statuses.data])
  // Önem Derecesi filtre seçenekleri REFERANS'tan (dile-duyarlı) — sabit i18n eşlemesi değil; ad değişince yansır.
  const levels = useActionLevels()
  // Statü seçenekleri /statuses ucundan (REFERANS) gelir. 085 — İptal statüsü kalktı; eskiden yalnız
  // "Tüm Aksiyonlar"a elle eklenen İptal seçeneği de kaldırıldı.
  const statusOptions = useMemo(
    () => statusList.map((s) => ({ label: s.name, value: s.id })),
    [statusList],
  )
  const importanceOptions = useMemo(
    () => (levels.data?.importanceLevels ?? []).map((l) => ({ label: l.name, value: l.id })),
    [levels.data],
  )
  // Seçenekler kapsamda O ROLDE atanmış kişilerden gelir (sunucu); ekranın diğer süzgeçlerinden
  // etkilenmez → filtre değiştikçe seçenek kaybolmaz. Sicil bazında tekil geldiği için ek dedup gerekmez.
  // İki AYRI istek: her kutu yalnız kendi rolündekileri listeler, yoksa kullanıcı "Ana Sorumlu"
  // kutusundan yalnızca SDY olan birini seçip sebebi görünmeyen boş bir liste alırdı.
  const mainResponsibles = useResponsibleOptions(scope, responsibleFilterApplicable, 'main')
  const supportManagers = useResponsibleOptions(scope, responsibleFilterApplicable, 'support')
  const toPersonOptions = (people: MinutePerson[] | undefined) =>
    (people ?? []).map((p) => ({ label: p.fullName?.trim() || p.employeeId, value: p.employeeId }))
  const mainResponsibleOptions = useMemo(
    () => toPersonOptions(mainResponsibles.data),
    [mainResponsibles.data],
  )
  const supportManagerOptions = useMemo(
    () => toPersonOptions(supportManagers.data),
    [supportManagers.data],
  )
  // Seçenekler VERİYE bağlıdır (diğer süzgeçlerin sabit listelerinden farklı): saklanan seçim, o kişi
  // artık hiçbir aksiyonun Ana Sorumlusu değilse listede bulunamaz. Dropdown bu durumda placeholder
  // gösterir ama istek süzgeçle gider → kullanıcı sebebi görünmeyen boş bir liste görürdü.
  useEffect(() => {
    if (!mainResponsibles.isSuccess || mainResponsibleEmployeeId == null) return
    if (!mainResponsibleOptions.some((o) => o.value === mainResponsibleEmployeeId)) {
      setMainResponsibleEmployeeId(null)
      setFirst(0)
    }
  }, [mainResponsibles.isSuccess, mainResponsibleOptions, mainResponsibleEmployeeId])
  useEffect(() => {
    if (!supportManagers.isSuccess || supportManagerEmployeeId == null) return
    if (!supportManagerOptions.some((o) => o.value === supportManagerEmployeeId)) {
      setSupportManagerEmployeeId(null)
      setFirst(0)
    }
  }, [supportManagers.isSuccess, supportManagerOptions, supportManagerEmployeeId])

  /**
   * İki kişi süzgecinin ortak çizimi (Ana Sorumlu · Süreç Destek Yöneticisi). Bileşen DEĞİL, düz bir
   * render yardımcısı: bileşen olarak tanımlansaydı her render'da yeni bir tip üretir ve PrimeReact
   * açılır listesi (açık paneliyle birlikte) yeniden monte edilirdi.
   */
  const personFilter = (
    value: string | null,
    options: Array<{ label: string; value: string }>,
    placeholder: string,
    onSelect: (next: string | null) => void,
  ) => (
    <MobileSheetDropdown
      key={placeholder}
      value={value}
      options={options}
      onChange={(e) => {
        const next = (e.value as string | null) ?? null
        // Temizleme ("X") satır navigasyonunu tetikleyen hayalet tıka açıktır — bkz. suppressRowClickBriefly.
        if (next == null) suppressRowClickBriefly()
        onSelect(next)
        setFirst(0)
      }}
      placeholder={placeholder}
      filter
      // Arama Türkçe-farkında olmak ZORUNDA (Anayasa): yerel verilmezse PrimeReact tarayıcı
      // yereliyle küçültür ve "ismail" yazınca "İsmail Çakır" eşleşmez (AssignmentPickList deseni).
      filterLocale={filterLocale}
      filterPlaceholder={t('actions.filter.responsibleSearch')}
      emptyMessage={t('actions.filter.responsibleEmpty')}
      emptyFilterMessage={t('actions.filter.responsibleEmpty')}
      showClear
      className="act-filter"
      // Figma'nın kişi bottom-sheet'inin UX niyeti — dar ekranda arama + uzun listeye başparmak
      // mesafesinde erişim (bkz. ui.css `.ui-mobile-sheet`). Görsel/etkileşim olarak KOPYALANMAZ:
      // seçim yine anında uygulanır, ayrı bir Uygula adımı yoktur.
      panelClassName="ui-mobile-sheet"
    />
  )

  // Panodan gelen süzgeçlerin chip'leri (bkz. panelChipsOf) — etiket üretimi saf fonksiyonda.
  const panelChips = panelChipsOf(
    { overdueOnly, upcomingOnly, delegatedOnly, period, periodFrom, periodTo },
    t,
  )
  /** Chip "kaldır" eylemleri; anahtar kümesi {@link panelChipsOf} ile aynıdır. */
  const clearPanelChip: Record<PanelChipKey, () => void> = {
    overdue: () => setOverdueOnly(false),
    upcoming: () => setUpcomingOnly(false),
    delegated: () => setDelegatedOnly(false),
    period: () => setPeriod(null),
  }

  const hasFilter =
    search.trim().length > 0 ||
    importanceLevelId != null ||
    statusFilter.length > 0 ||
    criticalOrOverdue ||
    overdueOnly ||
    upcomingOnly ||
    delegatedOnly ||
    period != null ||
    (kindApplicable && kind !== 'all') ||
    delayedUpdatedOnly ||
    mainResponsibleSicil != null ||
    supportManagerSicil != null
  const clearAll = () => {
    setSearch('')
    setImportanceLevelId(null)
    setStatusIds([])
    setCriticalOrOverdue(false)
    setOverdueOnly(false)
    setUpcomingOnly(false)
    setDelegatedOnly(false)
    setPeriod(null)
    setKind('all')
    setDelayedUpdatedOnly(false)
    setMainResponsibleEmployeeId(null)
    setSupportManagerEmployeeId(null)
    setFirst(0)
  }

  // Birim ("5 gün") HER İKİ yüzeyde de yazılır (kullanıcı isteği 2026-08-26): tabloda çıplak "5"
  // neyin beşi olduğunu söylemiyordu — kolon başlığı dar olduğu için ellipsis'e düşüp bağlamı da
  // veremiyor. Kolon genişliği %5 → %8'e çıkarıldı, aksi halde birim sığmaz ve harf harf bölünür.
  //
  // Tabloya bağlanırken sarmalayıcı ŞART: PrimeReact `body`'ye 2. argüman olarak ColumnBodyOptions
  // geçirir; doğrudan bağlanırsa `withUnit` yerine o nesne düşer. Parametre bilinçle KORUNDU —
  // gelecekte birimsiz bir yüzey gerekirse (ör. çok dar bir kart) tek çağrı noktasından kapatılır.
  const remainingBody = (row: ActionListItem, withUnit = true) => {
    const days = remainingDays(row.deadline, row.statusId)
    if (days == null) return ''
    const text = withUnit ? t('actions.columns.remainingValue', { count: days }) : String(days)
    if (days > 0) return <span className="act-remaining-ontime">{text}</span>
    if (days < 0) return <span className="act-overdue">{text}</span>
    // 0 gün = son gün: gecikmemiş (kırmızı değil), ama süresi de kalmamış (yeşil değil) → nötr gri
    // rozet. Önce rozetsiz düz metindi ve kolonda tek başına uyumsuz duruyordu.
    return <span className="act-remaining-today">{text}</span>
  }

  /*
   * Görev SİLME GERİ GELDİ (kullanıcı isteği 2026-09-17): "Tüm Görevler ve Oluşturduklarım
   * sayfasındaki tablolara silme butonu ekleyelim, onaylı olsun, bağımsızlar da silinebilsin."
   *
   * 2026-08-26'daki "aksiyonlarda silme özelliği olmayacak" kararı böylece BU İKİ EKRAN için
   * geçersizdir; kaldırılanlar (Sil kolonu · karttaki çöp kutusu · onay penceresi) geri kondu. Aradaki
   * dönemde silme yetkilendirmesi de değişti (2026-09-11): tek yetki `RECORD_DELETE`, sahiplik
   * (`CRE_BY`) ölçütü yok — ekran o modeli olduğu gibi kullanır ve satır kapısı için sunucunun
   * gönderdiği `deletable` alanını (yeniden) okur.
   *
   * BAĞIMSIZ GÖREVLER için ayrı bir kol YOKTUR ve gerekmez: uç (`DELETE /actions/{id}`) toplantı
   * bağlamına bakmaz, bağımsız görevde de denetim izi yazar (MEETING_ID null). Satırın toplantısı olup
   * olmaması burada hiçbir koşula girmez — istenen davranış tam olarak budur.
   *
   * Onay + mutasyon + geri bildirim `useActionListDelete`'tedir (bu bileşenin bilişsel karmaşıklığı
   * zaten yüksek; Toplantı Detay'da aynı sebeple `useMeetingRecordDelete` var).
   */

  return (
    <>
      <FilterBar
        className="ui-filterbar--wide-search act-filterbar"
        searchValue={search}
        searchPlaceholder={t('actions.searchPlaceholder')}
        searchAriaLabel={t('actions.searchPlaceholder')}
        onSearch={setSearch}
        showClear={hasFilter}
        clearLabel={t('actions.filter.clear')}
        onClear={clearAll}
      >
        <Dropdown
          value={importanceLevelId}
          options={importanceOptions}
          onChange={(e) => {
            setImportanceLevelId(e.value)
            setFirst(0)
          }}
          placeholder={t('actions.filter.importance')}
          showClear
          className="act-filter"
        />
        {/* Statü süzgeci ARŞİVDE YOK (kullanıcı kararı 2026-08-13): arşiv kümesi tek statüden
            (Arşivlendi) oluşuyor — İptal 085'te kalktıktan sonra süzgecin seçecek bir şeyi kalmadı. */}
        {!archived && (
          <MultiSelect
            value={statusIds}
            options={statusOptions}
            onChange={(e) => {
              setStatusIds(e.value)
              setFirst(0)
            }}
            placeholder={t('actions.filter.status')}
            // Panel başlığındaki "tümü" onay kutusu etiketsiz çizilir ve tek başına ne yaptığı
            // okunmaz (kullanıcı bildirimi 2026-08-30). PrimeReact bu etiketi YALNIZ `filter`
            // kapalıyken basar (multiselect.cjs.js: `!props.filter && <label ...>`) — bu süzgeçte
            // arama kutusu yok, o yüzden görünür. Arama açılırsa etiket sessizce kaybolur.
            //
            // MERGE NOTU (2026-08-30): bu satır iki taraftan birden geldi — `44a31b5c` (bu dalda,
            // `actions.filter.statusSelectAll` = "Tümünü Seç") ve THY senkronu `c446f88d`
            // (`actions.filter.all` = "Tümü"). Git ikisini de aldı, JSX çift attribute ile
            // derlenmiyordu. THY'ninki TUTULDU çünkü uygulama geneli desendir (aynı anahtar
            // ActivityFormPage ve DashboardFilters'ta da select-all etiketi); yalnız burada farklı
            // bir metin kullanmak üç ekran arasında tutarsızlık üretirdi.
            selectAllLabel={t('actions.filter.all')}
            showClear
            maxSelectedLabels={1}
            selectedItemsLabel={t('actions.filter.statusSelected')}
            className="act-filter"
          />
        )}
        {/* Kişi süzgeçleri — 2026-09-15'te AYRILDI (kullanıcı isteği): önce tek kutu vardı ve seçilen
            sicili Ana VEYA Yan rolünde arıyordu. Artık her rolün kendi kutusu var ve ikisi VE ile
            birleşir. İkisi de aynı yardımcıdan çizilir: davranış (Türkçe arama, temizleme, bottom-sheet)
            tek yerde kalsın. Karartma/kaydırma kilidi `MobileSheetDropdown`'da yaşar. */}
        {responsibleFilterApplicable && personFilter(mainResponsibleSicil, mainResponsibleOptions,
          t('actions.filter.mainResponsible'), setMainResponsibleEmployeeId)}
        {responsibleFilterApplicable && personFilter(supportManagerSicil, supportManagerOptions,
          t('actions.filter.supportManager'), setSupportManagerEmployeeId)}
        {/* 071 — Tür (Ana/Alt) filtresi: hem ana hem alt içeren kapsamlarda (Oluşturduklarım · Bana
            Atananlar — ikincisi 2026-08-17'de eklendi, bkz. kindApplicable). */}
        {kindApplicable && (
          <Dropdown
            value={kind === 'all' ? null : kind}
            options={[
              { label: t('actions.kind.main'), value: 'main' },
              { label: t('actions.kind.sub'), value: 'sub' },
            ]}
            onChange={(e) => {
              setKind((e.value as 'main' | 'sub' | null) ?? 'all')
              setFirst(0)
            }}
            placeholder={t('actions.filter.kind')}
            showClear
            className="act-filter"
          />
        )}
        {/* Arşivde "Geciken" filtresi (checkbox): yalnız IS_DELAYED_UPDATED=Evet satırlar (df7bd6a). Aktif
            listelerden bilinçle kaldırıldığından (7c580aa) yalnız arşivde gösterilir. */}
        {archived && (
          <label className="act-filter-check">
            <Checkbox
              inputId="f-delayed-updated"
              checked={delayedUpdatedOnly}
              onChange={(e) => {
                setDelayedUpdatedOnly(!!e.checked)
                setFirst(0)
              }}
            />
            <span>{t('actions.filter.delayedUpdatedArchived')}</span>
          </label>
        )}
        {/* 053: Anasayfa panosundan gelen "kritik veya geciken" filtresi — görünür ve kaldırılabilir. */}
        {criticalOrOverdue && (
          <button
            type="button"
            className="act-chip-filter"
            onClick={() => {
              setCriticalOrOverdue(false)
              setFirst(0)
            }}
          >
            {t('actions.filter.criticalOrOverdue')}
            <i className="pi pi-times" aria-hidden />
          </button>
        )}
        {/* 2026-08-21: Anasayfa panosunun "Tümünü Gör" süzgeçleri — görünür ve kaldırılabilir; hedef
            tablo kartla aynı kümeyi gösterirken kullanıcı NEDEN daraldığını da görür. */}
        {panelChips.map((chip) => (
          <button
            key={chip.key}
            type="button"
            className="act-chip-filter"
            onClick={() => {
              clearPanelChip[chip.key]()
              setFirst(0)
            }}
          >
            {chip.label}
            <i className="pi pi-times" aria-hidden />
          </button>
        ))}
      </FilterBar>

      {/* Figma (180561:29596) ve kullanıcı isteği 2026-08-26: "Yenile" ve "Excel'e Aktar" sayfa
          başlığı satırında, "Oluştur" butonunun SOLUNDA durur.

          Eski yorum "buton sayfa başlığına konamaz, hangi sekmeyi aktardığı belirsiz kalırdı"
          diyordu; o kaygı bu ekran için geçerli DEĞİL: dört alt sayfa ayrı rotalardır ve her
          rotada tek tablo vardır, dolayısıyla başlıktaki buton hep o rotanın tablosunu aktarır
          (panel `key={scope}` ile taze mount olur). Kaygı, bir sayfada BİRDEN ÇOK tablo bulunan
          ekranlar için geçerliliğini sürdürüyor — ama çözüm ayrı bir şerit DEĞİL: o kalıp
          (`.ui-table-toolbar`) 2026-08-27'de tamamen kalktı, sınıf da silindi. Sekmeli ekranlarda
          aksiyonlar AKTİF SEKMEYE göre çizilir (bkz. Şablon Parametreleri).

          Durum yukarı TAŞINMADI: dışa aktarım sorgusu aşağıdaki tüm filtre durumuna bağlı ve
          taşımak 2026-08-21'de yaşanan "ekranda 3 satır, dosyada tüm zamanlar" hatasını geri
          davet ederdi. Portal yalnız ÇİZİM YERİNİ taşır. */}
      <PageHeaderActionsSlot>
        <RefreshButton onRefresh={() => list.refetch()} />
        <ExcelExportButton
          onExport={() => {
            // subAction: ekrandaki Tür süzgecinin aynısı (Oluşturduklarım/Bana Atananlar) → indirilen dosya
            // da ekranla aynı süzülür.
            const query = {
              scope,
              q: debounced,
              importanceLevelId,
              statusIds: statusFilter,
              subAction,
              delayedUpdated: delayedUpdatedOnly,
              sort,
            }
            return archived
              ? exportArchivedActions(query)
              : exportActions({
                  ...query,
                  criticalOrOverdue,
                  // Pano süzgeçlerinin TAMAMI dosyaya da gider (2026-08-21): biri düşerse ekranda 3
                  // satır görünürken indirilen dosyada tüm zamanların kayıtları çıkardı.
                  overdueOnly,
                  upcomingOnly,
                  delegatedOnly,
                  period: period ?? undefined,
                  periodFrom,
                  periodTo,
                  mainResponsibleEmployeeId: mainResponsibleSicil,
                  supportManagerEmployeeId: supportManagerSicil,
                })
          }}
          disabled={items.length === 0}
        />
      </PageHeaderActionsSlot>

      {/* Dar ekranda 11 kolonlu `tk-table-fit` tablosu her hücreyi ellipsis'e zorlar ve okunamaz
          hâle gelir (bkz. actions.css üst notu) — kaydırmasız-sığma kararı (kullanıcı isteği
          2026-08-07) masaüstü içindi, dar ekranı hedeflemiyordu. O yüzden yalnız dar ekranda aynı
          veri/sayfalama/silme ile kart listesine dönülür; eşiğin ÜSTÜNDE bu dal hiç render edilmez
          ve tablo/`tk-table-fit` kararı aynen korunur.

          Eşik tek kaynaktan gelir: `CARD_VIEW_BREAKPOINT` (lib/useMediaQuery) = max-width 1024px,
          yani tabletler de kart görünümündedir. Buraya piksel değeri YAZILMAZ — sabit yazıldığı
          sürece JS eşiği ile CSS media query'leri birbirinden sessizce ayrışır. */}
      {isMobile ? (
        <div className="act-cards">
          {list.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="act-card act-card--skeleton" aria-hidden />
            ))}
          {!list.isLoading && items.length === 0 && (
            <EmptyState
              icon="pi pi-check-square"
              title={t('actions.empty.title')}
              description={t('actions.empty.desc')}
            />
          )}
          {!list.isLoading &&
            items.map((row) => {
              const person = row.subAction
                ? subActionResponsibleOf(row.assignees)
                : mainResponsibleOf(row.assignees)
              const secondary = secondaryResponsiblesOf(row.assignees)
                .map((p) => p.fullName)
                .filter(Boolean)
                .join(', ')
              const detailPath = `${archived ? '/minutes/archived/actions' : '/minutes/actions'}/${row.id}`
              const meeting = meetingLabel(row)
              return (
                // **overlay-link** (anayasa → dar ekran kart kalıbı): kart kökü handler'sız düz bir
                // <div>; detayı başlıktaki GERÇEK kontrol açar ve kartı `::after` ile kaplar
                // (`ui.css` → `.ui-card--linked`/`.ui-card-link`; kartın görünümü `.act-card`ta kalır).
                // Sil düğmesi o kontrolün KARDEŞİdir.
                //
                // Kart bu biçime Sil düğmesi eklenirken geçirildi (2026-09-17, push kapısı review
                // bulgusu): kök `role="button"` + `tabIndex` taşırken düğme onun İÇİNDE kalıyordu ve
                // ARIA'da interaktif kontrol başka bir interaktif kontrol içeremez. Düğmedeki
                // `stopPropagation` bunu ÇÖZMEZ — yalnız fare davranışını düzeltir, semantiği değil
                // (THY inceleme bulgusu, `ceo-frontend` PR #33). Kökte handler kalmadığı için o
                // eleme de kalktı.
                <div key={row.id} className="act-card ui-card--linked">
                  {/* Statü solda, yıldız + Sil sağ kenarda (`.act-card-row` → space-between). Satır
                      koşulları (ana görev · `deletable`) bileşenin içinde; ikisi de yoksa boş kalır. */}
                  <div className="act-card-row">
                    <ActionStatusTag statusId={row.statusId} name={row.statusName ?? ''} />
                    <span className="act-card-actions">
                      <ActionListRowActions row={row} onDelete={onDelete} followEnabled={followEnabled} />
                    </span>
                  </div>
                  <div className="act-card-head">
                    {/* `<Link>` DEĞİL `<button>`: gezinme `goToDetail`ten geçmek zorunda — o, süzgeç
                        temizlemenin ürettiği "hayalet tık" penceresini yutuyor (bkz.
                        `suppressRowClickBriefly`). Bir <Link> o pencereyi atlar ve dokunmatikte
                        istenmeyen gezinme geri gelirdi. */}
                    <button
                      type="button"
                      className="ui-card-link"
                      onClick={() => goToDetail(detailPath)}
                    >
                      {/* Tipografi İÇ span'de kalır (`ActionViewCards` ile aynı): `.ui-card-link`
                          bilinçle tipografi taşımaz ve `color: inherit` verir — başlık sınıfını
                          düğmeye yazmak, iki dosyanın (ui.css ↔ actions.css) paket sırasına bağlı
                          bir renk yarışı açardı. */}
                      <span className="act-card-name">{row.name}</span>
                    </button>
                  </div>
                  {meeting && <div className="act-card-meeting">{meeting}</div>}

                  {(person?.fullName || secondary) && (
                    <div className="act-card-people">
                      {person?.fullName && <span title={person.fullName}>{person.fullName}</span>}
                      {secondary && (
                        <span className="act-card-secondary" title={secondary}>
                          + {secondary}
                        </span>
                      )}
                    </div>
                  )}
                  {/* Termin + kalan süre, sorumlu koşulunun DIŞINDADIR. İçindeyken sorumlusu
                      atanmamış bir aksiyonda listenin en kritik iki bilgisi hiç görünmüyordu;
                      masaüstü tabloda ikisi de ayrı kolonda olduğu için sorun yalnız karttaydı. */}
                  <div className="act-card-remaining">
                    {row.deadline && (
                      <span className="act-card-deadline">{formatDate(row.deadline)}</span>
                    )}
                    {!archived && remainingBody(row, true)}
                  </div>
                </div>
              )
            })}
          {!list.isLoading && total > 0 && (
            <Paginator
              first={first}
              rows={rows}
              totalRecords={total}
              onPageChange={(e: PaginatorPageChangeEvent) => {
                setFirst(e.first)
                setRows(e.rows)
              }}
              template="PrevPageLink CurrentPageReport NextPageLink"
              currentPageReportTemplate={t('common.pageReport')}
              className="act-cards-paginator"
            />
          )}
        </div>
      ) : (
        /* `tk-table-fit`: tablo **kaydırmasız** sığar (kullanıcı isteği 2026-08-07). Düzen `fixed` olduğu
          için kolon genişlikleri **yüzde** verilir (toplam 100); yüzdeler oransal normalize edildiğinden
          yetkiye/kapsama göre gizlenen kolonlar (Kayıt No, Tür, Kalan Süre, Sil) toplamı bozduğunda da
          taşma olmaz. `min-width` verilmez — sabit düzende etkisizdir, kolonu büyüten tek şey payıdır.

          Paylar **en uzun sözcüğe göre** dağıtıldı, göz kararı değil: harf ortasından bölünme (kullanıcı:
          "Alt aksiyo/n") her zaman payın en uzun sözcük + 24px hücre dolgusundan dar kalmasıdır. Atomik
          kolonlar (Kayıt No, Önem, Kalan Süre, Termin, Statü) tek satır + ellipsis kalır; **sarması
          gerekenler** `tk-cell-wrap` ile açıkça işaretlenir: Tür ("Ana | Aksiyon"), Toplantı, Aksiyon
          Başlığı, Sorumlu — hepsi boşluk içerdiği için kelime sınırında sarar. */
        <DataTable
          className="tk-table-fit"
          value={items}
          loading={list.isLoading}
          lazy
          first={first}
          rows={rows}
          totalRecords={total}
          onPage={(e) => {
            setFirst(e.first)
            setRows(e.rows)
          }}
          onSort={(e) => {
            setSortField(e.sortField)
            setSortOrder(e.sortOrder)
          }}
          sortField={sortField}
          sortOrder={sortOrder}
          onRowClick={(e) =>
            goToDetail(
              `${archived ? '/minutes/archived/actions' : '/minutes/actions'}/${(e.data as ActionListItem).id}`,
              // origin (path + query) useNavigateWithOrigin ile otomatik taşınır → Aksiyon Detay'da "Geri" buraya döner.
            )
          }
          rowClassName={() => 'mtg-row-clickable'}
          emptyMessage={
            <EmptyState
              icon="pi pi-check-square"
              title={t('actions.empty.title')}
              description={t('actions.empty.desc')}
            />
          }
        >
          {/* Pay %11'de KALDI (2026-09-11): "Önem Derecesi"nin ihtiyacı önce buradan karşılanmış, sonra
            ölçüm sonucuna göre "Hedef Teslim Tarihi"ne kaydırılmıştı — numara 14 karaktere kadar
            çıkabiliyor (`AA-G-2026-0145`) ve %8'de dar pencerede kırpılıyordu. `title` yine de durur:
            sabit düzende hiçbir atomik değer kırpılmayacağının garantisi yoktur. */}
          {showRecordNumber && showColumn('number') && (
            <Column
              field="recordNumber"
              header={t('actions.columns.number')}
              sortable
              headerStyle={{ width: '11%' }}
              body={(row: ActionListItem) => (
                <span title={row.recordNumber ?? undefined}>{row.recordNumber ?? ''}</span>
              )}
            />
          )}
          {/* Ana/Alt aksiyon işareti — yalnız "Oluşturduklarım" (071) kök + alt aksiyonu birlikte içerir → kolon
            orada anlamlı. "Bana Atananlar" yalnız alt aksiyon, diğer kapsamlar yalnız kök gösterir → kolon gereksiz. */}
          {kindApplicable && showColumn('kind') && (
            <Column
              header={t('actions.columns.kind')}
              headerStyle={{ width: '8%' }}
              bodyClassName="tk-cell-wrap"
              body={(row: ActionListItem) =>
                t(row.subAction ? 'actions.kind.sub' : 'actions.kind.main')
              }
            />
          )}
          {scope === 'delegation' && showColumn('delegationKind') && (
            <Column
              // AYRI anahtar: `actions.columns.kind` aynı paneldeki Ana/Alt Görev kolonunundur —
              // paylaşılsaydı o kolonun başlığı da değişirdi (push kapısı review bulgusu).
              header={t('actions.columns.delegationKind')}
              headerStyle={{ width: '8%' }}
              /*
               * "Tür" — satır iki kümeden hangisinden geldi? (2026-08-30)
               *
               * Ölçüt: sicilim satırın SORUMLULARI arasında mı. Devir, sorumluyu KARŞI TARAFA geçirir
               * (eski atama soft-delete, yenisi insert — `setSubActionAssignee`), yani devrettiğim bir
               * satırda sicilim artık yoktur. İki küme bu yüzden AYRIKtır; canlı veride de doğrulandı
               * (kesişim 0). Sunucudan ayrı bir bayrak İSTENMEDİ: liste sorgusunun projeksiyonuna alan
               * eklemek, uygulamanın en karmaşık sorgusuna dokunmak demekti.
               *
               * SINIR: kesişim doğarsa (aynı görevde hem sorumlu hem devreden) satır "Bana Atanan"
               * görünür. Kümeye girme sebebi zaten sağlanmıştır, yani satır kaybolmaz.
               */
              body={(row: ActionListItem) =>
                t(
                  (row.assignees ?? []).some((a) => a.employeeId === me?.employeeId)
                    ? 'home.my.delegation.assignedToMe'
                    : 'home.my.delegation.delegated',
                )
              }
            />
          )}
          {showColumn('importance') && (
            <Column
              header={
                <span title={t('actions.columns.importance')}>{t('actions.columns.importance')}</span>
              }
              sortable
              sortField="importanceLevelId"
              // 2026-09-11 (kullanıcı isteği: "başlık sığmıyor"): pay %7 → %10. Üç puan **hedef teslim
              // tarihi** kolonundan alındı (önce Görev No'dan alınmıştı, ölçüm sonrası kaydırıldı):
              // oradaki değer SABİT genişliktedir (`21.07.2026` ≈ 86px) ve %8'de tam sığar, oysa görev
              // numarası 14 karaktere çıkıp %8'de kırpılıyordu. `act-th-tight` de KALKTI — o değiştirici
              // başlığı tek satıra kilitleyip kırpıyordu ("Önem Derece…"), yani sorunun kendisiydi.
              // %10'da başlık iki satıra temiz sarılır ("Önem" / "Derecesi"): en uzun sözcük ~52px,
              // kolonun metin payı dar pencerede bile (~960px tablo) ~54px. 2026-08-20'de harften bölme
              // (“Derece/si”) yaşanmasının sebebi %7'de payın 40px'e düşmesiydi; o sebep kalktı.
              headerStyle={{ width: '10%' }}
              body={(row: ActionListItem) => (
                <ImportanceBadge levelId={row.importanceLevelId} name={row.importanceLevelName} />
              )}
            />
          )}
          {showColumn('meeting') && (
            <Column
              header={t('actions.columns.meeting')}
              sortable
              sortField="meetingName"
              headerStyle={{ width: '10%' }}
              bodyClassName="tk-cell-wrap"
              body={(row: ActionListItem) => meetingLabel(row)}
            />
          )}
          {/* En geniş pay başlığa: listenin birincil tanımlayıcısı odur ve tek satıra sığmayacağı bilinir. */}
          {showColumn('name') && (
            <Column
              field="name"
              header={t('actions.columns.name')}
              sortable={!contentMasked}
              headerStyle={{ width: '13%' }}
              bodyClassName="tk-cell-strong tk-cell-wrap"
            />
          )}
          {/* "Görev Detayı" — yalnız yönetici kolon kümesinde (kullanıcı kuralı 2026-09-10); diğer
            rollerin tablosunda böyle bir kolon yok. Metin 2000 karaktere kadar olabildiğinden hücre
            `act-cell-desc` ile en çok 3 satıra kırpılır — sarmaya bırakılsa tek bir satır tabloyu ekran
            boyu uzatırdı; tam metin `title` ipucunda ve satıra tıklayınca Görev Detay'da.

            Sıralama SUNUCUDA (kullanıcı isteği 2026-09-10): `sortField="description"` →
            `ACTION_SORT_EXPR` Türkçe anahtara (`trsort`) eşler ve boş hücreleri iki yönde de sona
            toplar. İstemcide sıralanamaz — liste sayfalıdır, görünen 20 satırı sıralamak kullanıcıya
            "tüm görevler sıralandı" izlenimi verirken yanlış sonucu gösterirdi. */}
          {executiveView && (
            <Column
              header={t('actions.columns.description')}
              sortable={!contentMasked}
              sortField="description"
              headerStyle={{ width: '22%' }}
              bodyClassName="tk-cell-wrap"
              body={(row: ActionListItem) => (
                <span className="act-cell-desc" title={row.description ?? undefined}>
                  {row.description ?? ''}
                </span>
              )}
            />
          )}
          {/* Alt Görev Sayısı — yalnız "Tüm Görevler" (kullanıcı isteği 2026-09-10). Kolon SIRALANAMAZ:
            sunucunun sıralama eşlemesinde (`ActionQueryService.ACTION_SORT_EXPR`) bu alanın karşılığı yok,
            `sortable` vermek tutulmayacak bir söz olurdu. Kapsam neden dar: "Bana Atananlar" yalnız ALT
            görev listeler (hepsi 0 gelirdi), "Oluşturduklarım"/"Delegasyon" kök + alt görevi karıştırır.
            Sayı sunucudan gelir (`subActionCount`) ve Görev Detay'daki alt görev kartlarıyla aynı ölçütü
            kullanır — istemcide hesaplanmaz, liste satırı alt görevleri zaten taşımaz. */}
          {scope === 'all' && showColumn('subActionCount') && (
            <Column
              header={t('actions.columns.subActionCount')}
              headerStyle={{ width: '8%' }}
              body={(row: ActionListItem) => row.subActionCount}
            />
          )}
          {/* Sorumlu(lar) — MINUTES_ASSIGNEE (sicil bazında çoklu; ad-soyad birleşik). Sıralama: alfabetik ilk sorumlu. */}
          {showColumn('mainResponsible') && (
            <Column
              header={t('actions.columns.mainResponsible')}
              sortable
              sortField="assignee"
              headerStyle={{ width: '10%' }}
              bodyClassName="tk-cell-wrap"
              body={(row: ActionListItem) => {
                // 2026-08-12: Sorumlu kolonu Ana/Yan olarak ikiye ayrıldı. `sortField` "assignee" kalır —
                // sunucu sıralaması sorumlu adları üzerinden yapılır (rol bazlı sıralama ucu yok).
                //
                // Alt aksiyon satırında ölçüt AYRIDIR (kullanıcı isteği 2026-08-18): alt aksiyonun tek
                // atananı bu kolonda görünür. Role bakan ölçüt o satırlarda hücreyi boş bırakıyordu —
                // atanan rolsüz yazılmış eski kayıtlar canlı veride çoğunluk. Gerekçe ve "Yan Sorumlu
                // dışlanır" kuralı: subActionResponsibleOf.
                const person = row.subAction
                  ? subActionResponsibleOf(row.assignees)
                  : mainResponsibleOf(row.assignees)
                const name = person?.fullName ?? ''
                // `act-cell-clip` (tek satır + ellipsis) burada KULLANILMAZ: bu listede hedef kaydırmasız
                // sığmaktır, ellipsis ise adları gizleyerek "kim sorumlu" sorusunu cevapsız bırakırdı.
                // `title` yine durur — sarılan uzun listede de tam metin ipucu işe yarar.
                //
                // Baş harf rozeti (Figma 180561:29596; kullanıcı isteği 2026-08-26 — "sorumlu isimlerinin
                // baş harfleri eksik"): paylaşılan `Avatar` kullanılır, pano tablolarıyla aynı bileşen.
                // Ad boşsa rozet de çizilmez (bileşen boş baş harfte null döner) — yer tutucu bırakmayız.
                if (name === '') return null
                return (
                  <span className="ui-person" title={name}>
                    <Avatar name={name} className="ui-avatar--sm" />
                    {name}
                  </span>
                )
              }}
            />
          )}
          {/* Yan Sorumlu(lar) — 0..n; virgülle listelenir. SIRALANAMAZ: sunucudaki `assignee` sıralaması
            rol ayrımı yapmaz, "Yan Sorumluya göre sırala" diye bir uç yok — sortable vermek kullanıcıya
            tutmayacağı bir söz verirdi. */}
          {showColumn('secondaryResponsibles') && (
            <Column
              header={t('actions.columns.secondaryResponsibles')}
              headerStyle={{ width: '10%' }}
              bodyClassName="tk-cell-wrap"
              body={(row: ActionListItem) => {
                const people = secondaryResponsiblesOf(row.assignees).filter((p) => p.fullName)
                if (people.length === 0) return null
                // Virgülle birleştirilmiş tek metin YERİNE kişi başına rozet+ad: rozet ancak kendi adının
                // yanında durursa bilgi taşır. `title` tam listeyi yine verir (sarma olsa da ipucu okunur).
                const names = people.map((p) => p.fullName).join(', ')
                return (
                  <span className="ui-person-list" title={names}>
                    {people.map((p) => (
                      <span key={p.employeeId ?? p.fullName} className="ui-person">
                        <Avatar name={p.fullName} className="ui-avatar--sm" />
                        {p.fullName}
                      </span>
                    ))}
                  </span>
                )
              }}
            />
          )}
          {/* Kalan Süre — arşiv listesinde gösterilmez (arşivlenen aksiyonlar sonlanmıştır). */}
          {!archived && showColumn('remaining') && (
            <Column
              header={
                <span title={t('actions.columns.remaining')}>{t('actions.columns.remaining')}</span>
              }
              sortable
              // Kendi alanı — `deadline` DEĞİL. İki kolon aynı `sortField`i paylaşınca PrimeReact
              // sıralama göstergesini ikisine birden koyuyor ve kalan süreye göre bağımsız sıralama
              // yapılamıyordu (kullanıcı bildirimi 2026-08-26). Sunucu eşlemesi:
              // `ActionQueryService.ACTION_SORT_EXPR` → hedef teslim tarihi (kalan süre onunla tek yönlü
              // artan ilişkidedir). Hücresi BOŞ olan satırlar (termin yok VEYA sonlanmış statü — aşağıdaki
              // `remainingBody`/`remainingDays` ile aynı kural) sunucuda ayrı bir 0/1 bayrağıyla, yönden
              // bağımsız olarak DAİMA en sonda ve bir arada gelir (`ACTION_SORT_EXPR.remainingEmpty`).
              // Önce yalnız termine göre sıralanıyorlardı ve boş hücreler dolu olanların arasına
              // serpiştiriliyordu (kullanıcı bildirimi 2026-08-28). Bayrak kullanılır çünkü NULL
              // sıralaması veritabanına bağlıdır (H2 ASC'de başa, Oracle sona koyar).
              sortField="remaining"
              // 2026-09-11 (kullanıcı isteği: "ekranı küçültünce başlık alt satıra geçmiyor"):
              // `act-th-tight` KALDIRILDI — başlığı tek satıra kilitleyip kırpıyordu ("Kalan Sü…").
              // Artık iki satıra sarar ("Kalan" / "Süre"). 2026-08-20'de harf harf bölünmesinin sebebi
              // kolonun o günkü %5'lik payıydı (metin payı ~40px, "Kalan" ~34px sınırda); pay 2026-08-26'da
              // %8'e çıktı → 1150px'lik tabloda metin payı ~51px, en uzun sözcük ~34px. Sarma temiz.
              headerStyle={{ width: '8%' }}
              body={(row: ActionListItem) => remainingBody(row)}
            />
          )}
          {showColumn('deadline') && (
            <Column
              header={t('actions.columns.deadline')}
              sortable
              sortField="deadline"
              // 2026-09-11: %11 → %8, üç puan "Önem Derecesi"ne gitti (kullanıcı kararı). Ölçüm: değer
              // SABİT genişliktedir (`gg.aa.yyyy` ≈ 62px + 24px dolgu = 86px) ve %8 (~93px) onu tam
              // gösterir; başlık zaten iki-üç satıra sarıyordu, o değişmedi. Payı buradan almak, metin
              // sarmayan atomik değeri olan tek "geniş" kolon olduğu için bedelsizdir — diğer adaylar ya
              // sarılan metin (Görev Başlığı, Toplantı Adı, Durum) ya da başlığı zaten sınırda olan dar
              // kolonlardı (Kalan Süre, Alt Görev Sayısı).
              headerStyle={{ width: '8%' }}
              body={(row: ActionListItem) => (row.deadline ? formatDate(row.deadline) : '')}
            />
          )}
          {showColumn('status') && (
            <Column
              header={t('actions.columns.status')}
              sortable
              sortField="meetingActionStatusId"
              headerStyle={{ width: '11%' }}
              body={(row: ActionListItem) => (
                <ActionStatusTag statusId={row.statusId} name={row.statusName ?? ''} />
              )}
            />
          )}
          {/* Arşivde "Geciken" kolonu: IS_DELAYED_UPDATED=Evet (Güncellendi + termini geçmiş) → "Evet" (df7bd6a).
            Aktif listelerden bilinçle kaldırıldığından (7c580aa) yalnız arşivde gösterilir. */}
          {archived && showColumn('delayedUpdatedArchived') && (
            <Column
              header={t('actions.columns.delayedUpdatedArchived')}
              body={(row: ActionListItem) =>
                row.delayedUpdated ? (
                  <span className="act-overdue-label">{t('actions.delayedUpdated.yes')}</span>
                ) : (
                  ''
                )
              }
            />
          )}
          {/* Eylem kolonu — yıldız (120; dört görev sekmesinde, `useListFollowEnabled`) + Sil (yetki +
            kapsam + arşiv değil; `useActionListDelete`). Satır koşulları (ana görev · `deletable`) ve
            `stopPropagation` `ActionListRowActions`'tadır; kart aynı bileşeni çizer. Başlık BİLEREK
            boş: eylem kolonudur, ayırt edici metin düğmelerin `aria-label`ındadır ve satırın adını
            taşır. Pay %5 → %7: iki ikon yan yana sığsın. */}
          {hasListRowActions(onDelete, followEnabled) && (
            <Column
              header=""
              headerStyle={{ width: '7%' }}
              bodyStyle={{ textAlign: 'center' }}
              body={(row: ActionListItem) => (
                <ActionListRowActions row={row} onDelete={onDelete} followEnabled={followEnabled} />
              )}
            />
          )}
        </DataTable>
      )}
      {/* Onay penceresi — `confirmDialog({ group })` çağrısının hedefi; tetiklenmedikçe hiçbir şey
          çizmez, o yüzden Toplantı Detay'daki gibi KOŞULSUZ monte edilir. */}
      <ConfirmDialog group={ACTION_LIST_DELETE_GROUP} />
    </>
  )
}
